import { Content } from "@patternfly/react-core";
import { FormContainer } from "@rhc-shared-components/form";
import { FormCheckbox } from "@rhc-shared-components/form-checkbox";
import { FormRadio } from "@rhc-shared-components/form-radio-select";
import { FormSelect } from "@rhc-shared-components/form-select-component";
import { Formik } from "formik";
import { type FC, forwardRef } from "react";
import { ProjectContainerTypes, ProjectTypes } from "../../../../constants/project";
import { useVendorDetails } from "../../../../queries/productDetails";
import { useCreateModalStore } from "../CreateModalStore";
import {
	ContainerCreateInitialValues,
	ContainerCreateValidators,
	CreateFormKeys,
	getDistributionMethodType,
	OSContentTypes,
	ProjectContainerKeys,
	ProjectCreateModalKeys,
	useCreateAndAttachComponent,
} from "../createConstants";

interface ContainerCreateFormProps {
	handleCreateModal: (showModal: boolean) => void;
	ref: any;
}

export const ContainerCreateForm: FC<ContainerCreateFormProps> = forwardRef((props, ref: any) => {
	const { createNewComponent } = useCreateAndAttachComponent(props.handleCreateModal, "new");
	const { modalData } = useCreateModalStore();
	const vendorDetails = useVendorDetails();
	return (
		<Formik
			innerRef={ref}
			initialValues={ContainerCreateInitialValues}
			validate={(values) => {
				if (ref.current) {
					if (
						values[CreateFormKeys.osContentTypes] === "Red Hat Enterprise Linux" &&
						values[CreateFormKeys.distributionMethod] === "Your own Container Registry"
					) {
						ref.current.setFieldValue(CreateFormKeys.distributionMethod, "", false);
					}
				}
			}}
			validationSchema={ContainerCreateValidators}
			onSubmit={async (values) => {
				const distributionMethod = getDistributionMethodType().filter(
					(item) => item.value === values[CreateFormKeys.distributionMethod],
				)[0].key;
				const requestData = {
					[ProjectCreateModalKeys.name]: modalData.componentName,
					[ProjectCreateModalKeys.container]: {
						[ProjectContainerKeys.distributionMethod]: distributionMethod,
						[ProjectContainerKeys.osContentType]: values[CreateFormKeys.osContentTypes],
						[ProjectContainerKeys.type]: ProjectContainerTypes.container,
						[ProjectCreateModalKeys.allowHostedRegistry]: values[CreateFormKeys.allowHostedContainerRegistry],
					},
					[ProjectCreateModalKeys.marketplace]: {
						enablement_status: "ineligible",
					},
					[ProjectCreateModalKeys.type]: ProjectTypes.containers,
					[ProjectCreateModalKeys.projectStatus]: "active",
				};
				await createNewComponent(requestData);
			}}
		>
			{({ values }) => (
				<FormContainer gridSpan={12}>
					<FormRadio
						name={CreateFormKeys.osContentTypes}
						label={"OS Content Type"}
						subLabel={<div>What base image does your container use?</div>}
						subInfo={"The following values can't be changed after the project has been created"}
						isRequired={true}
						options={OSContentTypes}
						span={12}
					/>
					<FormSelect
						isFullWidth
						name={CreateFormKeys.distributionMethod}
						placeholder={"Select registry"}
						label={"Select your preferred Distribution Method"}
						subLabel={
							<Content className={"sub-label"}>
								<Content component="p">
									This is the container registry where customers will pull your certified container images from. Your
									container images remain hosted in a registry that you manage.
								</Content>
							</Content>
						}
						subInfo={"The following values can't be changed after the project has been created"}
						selectOptions={getDistributionMethodType(values[CreateFormKeys.osContentTypes])}
						isRequired={true}
					/>
					{values[CreateFormKeys.distributionMethod] === "Red Hat Container Registry" &&
						vendorDetails?.allow_hosted_container_registry && (
							<FormCheckbox
								id="allow-hosted-container-registry"
								data-testid="allow-hosted-container-registry"
								name={CreateFormKeys.allowHostedContainerRegistry}
								label=""
								subLabel="I need Red Hat to host my registry"
							/>
						)}
				</FormContainer>
			)}
		</Formik>
	);
});
